/**
 * Pipeline Breakdown Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows proposals (active/aging/stale) and scheduled future work
 */
import { usePipelineBreakdown } from '@/hooks/useDashboardReports';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function PipelineBreakdown() {
  const { data, isLoading, error } = usePipelineBreakdown();
  const navigate = useNavigate();
  const [proposalsExpanded, setProposalsExpanded] = useState(true);
  const [scheduledExpanded, setScheduledExpanded] = useState(true);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load pipeline breakdown data
      </div>
    );
  }

  const proposals = data?.proposals || { active: [], aging: [], stale: [] };
  const scheduledWork = data?.scheduled_work || [];
  const summary = data?.summary || {
    total_pipeline: 0,
    proposal_count: 0,
    proposal_total: 0,
    scheduled_total: 0,
  };

  const allProposals = [...proposals.active, ...proposals.aging, ...proposals.stale];
  const hasData = allProposals.length > 0 || scheduledWork.length > 0;

  // Calculate regular/optional totals
  const totalRegularValue = allProposals.reduce((sum, p) => sum + (p.regular_value || 0), 0);
  const totalOptionalValue = allProposals.reduce((sum, p) => sum + (p.optional_value || 0), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${Math.round(value / 1000)}k`;
    }
    return `$${value}`;
  };

  const handleRowClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'aging':
        return 'bg-yellow-100 text-yellow-800';
      case 'stale':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="pipeline-breakdown">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Pipeline Breakdown
      </h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Pipeline</div>
          <div className="text-xl font-bold text-sky-600" data-testid="summary-total">
            {formatCurrency(summary.total_pipeline)}
          </div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Proposals</div>
          <div className="text-xl font-bold text-purple-600" data-testid="summary-proposals">
            {formatCurrency(summary.proposal_total)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatCurrencyShort(totalRegularValue)} regular · {formatCurrencyShort(totalOptionalValue)} optional
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Scheduled Work</div>
          <div className="text-xl font-bold text-green-600" data-testid="summary-scheduled">
            {formatCurrency(summary.scheduled_total)}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Proposal Count</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-count">
            {summary.proposal_count}
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="text-center py-12 text-gray-500">
          No pipeline data found
        </div>
      ) : (
        <div className="space-y-6">
          {/* Proposals Section */}
          {allProposals.length > 0 && (
            <div>
              <button
                onClick={() => setProposalsExpanded(!proposalsExpanded)}
                className="flex items-center w-full text-left text-md font-semibold text-gray-700 mb-2"
              >
                {proposalsExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <span className="ml-1">Proposals ({allProposals.length})</span>
                <span className="ml-2 text-purple-600">{formatCurrency(summary.proposal_total)}</span>
              </button>

              {proposalsExpanded && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Age</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allProposals.map((proposal) => (
                        <tr
                          key={proposal.project_id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleRowClick(proposal.project_id)}
                        >
                          <td className="px-4 py-3 text-sm">
                            <span className="font-medium text-gray-900">{proposal.project_name}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {proposal.client_name || 'No Client'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            <div className="font-semibold text-purple-600">
                              {formatCurrency(proposal.total_value)}
                            </div>
                            {(proposal.regular_value || proposal.optional_value) && (
                              <div className="text-xs text-gray-500">
                                {formatCurrencyShort(proposal.regular_value || 0)} reg · {formatCurrencyShort(proposal.optional_value || 0)} opt
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-600">
                            {proposal.days_since_sent} days
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getCategoryColor(proposal.category)}`}>
                              {proposal.category}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Scheduled Work Section */}
          {scheduledWork.length > 0 && (
            <div>
              <button
                onClick={() => setScheduledExpanded(!scheduledExpanded)}
                className="flex items-center w-full text-left text-md font-semibold text-gray-700 mb-2"
              >
                {scheduledExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <span className="ml-1">Scheduled Work ({scheduledWork.length} months)</span>
                <span className="ml-2 text-green-600">{formatCurrency(summary.scheduled_total)}</span>
              </button>

              {scheduledExpanded && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scheduled Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {scheduledWork.map((week) => (
                        <tr key={week.month} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {week.month_label}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                            {formatCurrency(week.scheduled_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
